import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ServiceModal from "../Components/Modal/ServiceModal";

const DevelopmentPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const services = [
    {
      title: "Conversion-Focused Landing Pages",
      desc: "Strategically designed pages engineered to turn visitors into leads, customers, and loyal users.",
      icon: "web",
      color: "from-blue-500/20 to-transparent",
      accent: "text-blue-500 bg-blue-500/10",
    },
    {
      title: "Scalable E-Commerce Systems",
      desc: "Performance-optimized online stores built for growth, automation, and seamless user journeys.",
      icon: "storefront",
      color: "from-green-500/20 to-transparent",
      accent: "text-green-500 bg-green-500/10",
    },
    {
      title: "Advanced API & System Integrations",
      desc: "Connect your business tools, CRMs, payment gateways, and platforms into one powerful ecosystem.",
      icon: "api",
      color: "from-purple-500/20 to-transparent",
      accent: "text-purple-500 bg-purple-500/10",
    },
    {
      title: "Experience-Driven UI/UX Design",
      desc: "Redesign your digital presence with interfaces that feel intuitive, modern, and effortless.",
      icon: "design_services",
      color: "from-pink-500/20 to-transparent",
      accent: "text-pink-500 bg-pink-500/10",
    },
    {
      title: "Brand Identity & Logo Systems",
      desc: "Crafting distinctive visual identities that position your brand with clarity and confidence.",
      icon: "draw",
      color: "from-amber-500/20 to-transparent",
      accent: "text-amber-500 bg-amber-500/10",
    },
    {
      title: "Ongoing Performance & Maintenance",
      desc: "Continuous optimization, updates, and security to keep your platform fast and future-ready.",
      icon: "build_circle",
      color: "from-cyan-500/20 to-transparent",
      accent: "text-cyan-500 bg-cyan-500/10",
    },
    {
      title: "High-Performance Hosting Infrastructure",
      desc: "Secure, reliable, and scalable hosting environments tailored for modern web applications.",
      icon: "dns",
      color: "from-indigo-500/20 to-transparent",
      accent: "text-indigo-500 bg-indigo-500/10",
    },
    {
      title: "Growth & Digital Strategy",
      desc: "Data-backed campaigns and funnel strategies designed to scale your reach and revenue.",
      icon: "campaign",
      color: "from-orange-500/20 to-transparent",
      accent: "text-orange-500 bg-orange-500/10",
    },
    {
      title: "Premium Creative Production",
      desc: "High-impact visuals, marketing creatives, and digital assets that elevate brand perception.",
      icon: "palette",
      color: "from-rose-500/20 to-transparent",
      accent: "text-rose-500 bg-rose-500/10",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.15 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.98 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: "spring", stiffness: 60, damping: 15 },
    },
  };

  return (
    <div className="min-h-screen bg-[#F6F8FB] font-sans selection:bg-[#E63946] selection:text-white overflow-x-hidden relative">
      {/* Dynamic Background Blobs */}
      <motion.div
        animate={{ scale: [1, 1.1, 1], rotate: [0, 5, -5, 0] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 left-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-[#E63946]/10 rounded-full blur-[120px] md:blur-[150px] -translate-x-1/2 -translate-y-1/2 pointer-events-none"
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], rotate: [0, -5, 5, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[30%] right-0 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-blue-500/5 rounded-full blur-[100px] md:blur-[120px] translate-x-1/3 pointer-events-none"
      />

      {/* --- HERO SECTION --- */}
      <section className="relative pt-28 sm:pt-32 pb-14 sm:pb-20 md:pb-32 mx-auto w-[95%] md:w-[90%] flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-16">
        {/* Left: Text Content */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="relative z-10 w-full lg:w-1/2 text-center lg:text-left flex flex-col items-center lg:items-start"
        >
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white shadow-sm border border-slate-200 mb-6 md:mb-8"
          >
            <span className="size-2.5 rounded-full bg-[#E63946] animate-pulse shadow-[0_0_10px_rgba(230,57,70,0.6)]"></span>
            <span className="text-[10px] font-black uppercase tracking-[0.2em] text-[#0B1C2D]">
              ContactHQ Studio
            </span>
          </motion.div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-[#0B1C2D] tracking-tighter leading-[1.1] mb-5 sm:mb-6 md:mb-8">
            We Don’t Just Build Websites. <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0B1C2D] via-[#E63946] to-[#E63946] animate-gradient-x">
              We Build Growth Engines.
            </span>
          </h1>

          <p className="text-slate-500 text-sm md:text-lg font-medium max-w-xl mb-10 md:mb-12 leading-relaxed">
            Your digital presence shouldn’t just look good — it should convert,
            scale, and perform. We design and engineer systems that turn
            attention into action and visitors into measurable growth.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsModalOpen(true)}
            className="group relative inline-flex items-center justify-center bg-[#0B1C2D] text-white px-8 md:px-10 py-4 md:py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl shadow-[#0B1C2D]/30 overflow-hidden w-full sm:w-auto"
          >
            <span className="relative z-10 flex items-center gap-2">
              Let’s Build Something Powerful{" "}
              <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">
                arrow_forward
              </span>
            </span>
            <div className="absolute inset-0 bg-[#E63946] translate-y-[100%] group-hover:translate-y-0 transition-transform duration-300 ease-in-out"></div>
          </motion.button>
        </motion.div>

        {/* Right: Visual Animated Graphic (Fixed Overlapping) */}
        <motion.div
          initial={{ opacity: 0, x: 40, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
          className="relative z-10 w-full max-w-[500px] lg:w-1/2 mx-auto lg:mx-0"
        >
          <div className="relative w-full aspect-square perspective-1000">
            {/* Core Visual Box */}
            <div className="absolute inset-2 md:inset-1 rounded-[20px] md:rounded-[20px] bg-white border border-slate-100 shadow-2xl overflow-hidden flex flex-col p-6 transform rotate-y-[-5deg] rotate-x-[5deg] hover:rotate-y-0 hover:rotate-x-0 transition-transform duration-700">
              <div className="absolute inset-0 bg-gradient-to-tr from-slate-50 to-transparent opacity-50 pointer-events-none"></div>

              {/* Mock UI Dashboard inside the box */}
              <div className="relative z-10 w-full h-full flex flex-col gap-4">
                {/* Mock Header */}
                <div className="flex justify-between items-center pb-4 border-b border-slate-100">
                  <div className="flex gap-2">
                    <div className="size-3 rounded-full bg-[#E63946]/20"></div>
                    <div className="size-3 rounded-full bg-amber-400/20"></div>
                    <div className="size-3 rounded-full bg-green-400/20"></div>
                  </div>
                  <div className="h-4 w-24 bg-slate-100 rounded-full"></div>
                </div>

                {/* Mock Chart Area */}
                <div className="flex-1 flex items-end gap-2 pt-4">
                  <motion.div
                    animate={{ height: ["40%", "70%", "40%"] }}
                    transition={{ duration: 4, repeat: Infinity }}
                    className="flex-1 bg-blue-100 rounded-t-xl"
                  ></motion.div>
                  <motion.div
                    animate={{ height: ["60%", "30%", "60%"] }}
                    transition={{ duration: 5, repeat: Infinity }}
                    className="flex-1 bg-[#E63946]/20 rounded-t-xl"
                  ></motion.div>
                  <motion.div
                    animate={{ height: ["80%", "100%", "80%"] }}
                    transition={{ duration: 6, repeat: Infinity }}
                    className="flex-1 bg-green-100 rounded-t-xl"
                  ></motion.div>
                  <motion.div
                    animate={{ height: ["50%", "80%", "50%"] }}
                    transition={{ duration: 4.5, repeat: Infinity }}
                    className="flex-1 bg-purple-100 rounded-t-xl"
                  ></motion.div>
                </div>
              </div>

              {/* Floating UI Element 1 */}
              <motion.div
                animate={{ y: [-10, 10, -10] }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="absolute top-4 -left-1 md:-left-2 bg-white p-3 md:p-4 rounded-2xl shadow-xl border border-slate-100 flex items-center gap-3 backdrop-blur-md z-20"
              >
                <div className="size-8 md:size-10 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                  <span className="material-symbols-outlined text-sm md:text-lg">
                    trending_up
                  </span>
                </div>
                <div>
                  <p className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-slate-400">
                    Conversion
                  </p>
                  <p className="text-sm md:text-lg font-black text-[#0B1C2D]">
                    +24.8%
                  </p>
                </div>
              </motion.div>

              {/* Floating UI Element 2 */}
              <motion.div
                animate={{ y: [10, -10, 10] }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1,
                }}
                className="absolute bottom-0 -right-1 md:-right-2 bg-[#0B1C2D] p-4 md:p-5 rounded-2xl md:rounded-3xl shadow-2xl border border-slate-700 flex flex-col gap-2 z-20"
              >
                <span className="material-symbols-outlined text-[#E63946] text-2xl md:text-3xl">
                  code_blocks
                </span>
                <p className="text-[9px] md:text-xs font-black uppercase tracking-widest text-white">
                  Scale Ready
                </p>
              </motion.div>
            </div>

            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] h-[90%] bg-[#E63946] blur-[80px] md:blur-[100px] opacity-20 -z-10 rounded-full"></div>
          </div>
        </motion.div>
      </section>

      {/* --- CREATIVE SERVICES GRID --- */}
      <section className="py-14 sm:py-20 md:py-24 mx-auto w-[95%] md:w-[90%]">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-16 gap-6 md:gap-8">
          <div>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-[#0B1C2D] tracking-tighter mb-4 md:mb-6 leading-tight">
              What We Can <br className="hidden md:block" /> Build For You
            </h2>
            <div className="w-16 md:w-24 h-1.5 md:h-2 bg-[#E63946] rounded-full"></div>
          </div>
          <p className="text-slate-500 font-medium max-w-md md:text-right text-sm md:text-base lg:text-lg leading-relaxed pb-2">
            From branding to backend architecture — everything required to
            launch and scale a serious digital business.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              whileHover={{ y: -8 }}
              className="relative bg-white p-6 md:p-8 lg:p-10 rounded-[32px] md:rounded-[40px] border border-slate-100 shadow-sm hover:shadow-[0_20px_50px_rgba(11,28,45,0.08)] transition-all duration-500 group overflow-hidden flex flex-col justify-between min-h-[280px] md:min-h-[320px]"
            >
              {/* Massive Watermark Icon in Background (Fixed Z-index) */}
              <span className="absolute -bottom-10 -right-8 text-[140px] md:text-[180px] material-symbols-outlined text-slate-50 opacity-50 group-hover:scale-110 group-hover:-rotate-12 transition-transform duration-700 pointer-events-none select-none z-0">
                {service.icon}
              </span>

              {/* Glowing Gradient Overlay */}
              <div
                className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0`}
              ></div>

              {/* Content Box (Strictly above background elements) */}
              <div className="relative z-10 flex flex-col h-full pointer-events-none">
                {/* Premium Glass Icon Box */}
                <div
                  className={`w-14 h-14 md:w-16 md:h-16 rounded-[16px] md:rounded-[20px] flex items-center justify-center mb-6 md:mb-8 backdrop-blur-xl border border-white/50 shadow-sm transition-transform duration-500 group-hover:scale-110 ${service.accent}`}
                >
                  <span className="material-symbols-outlined text-2xl md:text-3xl">
                    {service.icon}
                  </span>
                </div>

                <div className="mt-auto pointer-events-auto">
                  <h3 className="text-lg md:text-xl lg:text-2xl font-black text-[#0B1C2D] mb-3 md:mb-4 group-hover:text-[#E63946] transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-xs md:text-sm lg:text-base text-slate-500 font-medium leading-relaxed">
                    {service.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* --- PRICING BANNER --- */}
      <section className="py-14 sm:py-20 md:py-24 mx-auto w-[95%] md:w-[90%]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 40 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, type: "spring" }}
          className="bg-[#0B1C2D] rounded-3xl sm:rounded-[40px] md:rounded-[48px] p-6 sm:p-8 md:p-20 text-center relative overflow-hidden shadow-2xl border border-slate-800"
        >
          {/* Animated Tech Rings */}
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            className="absolute -top-[40%] -right-[10%] w-[400px] h-[400px] md:w-[600px] md:h-[600px] border border-white/5 rounded-full border-dashed pointer-events-none"
          />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%] h-32 md:h-48 bg-[#E63946] blur-[100px] md:blur-[150px] opacity-20 pointer-events-none rounded-t-full"></div>

          <div className="relative z-10 flex flex-col items-center">
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="w-16 h-16 md:w-20 md:h-20 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center mb-6 md:mb-8 border border-white/20 shadow-[0_0_30px_rgba(230,57,70,0.3)]"
            >
              <span className="material-symbols-outlined text-[#E63946] text-3xl md:text-4xl">
                currency_rupee
              </span>
            </motion.div>

            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter mb-4 md:mb-6 leading-tight">
              Strategic Investment. Clear Scope.
            </h2>
            <p className="text-slate-400 text-sm md:text-base lg:text-lg font-medium max-w-3xl mx-auto mb-8 md:mb-10 leading-relaxed px-4">
              Every project is unique. We define outcomes, deliverables, and
              measurable impact before we talk numbers. Transparent pricing.
              Zero surprises.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="bg-[#E63946] text-white px-8 md:px-10 py-4 md:py-5 rounded-2xl font-black text-xs md:text-sm uppercase tracking-widest shadow-[0_0_30px_rgba(230,57,70,0.4)] hover:bg-white hover:text-[#E63946] transition-colors duration-300 w-full sm:w-auto"
            >
              Start the Conversation
            </motion.button>
          </div>
        </motion.div>
      </section>

      {/* --- INQUIRY MODAL --- */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-[#0B1C2D]/80 backdrop-blur-md overflow-y-auto">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 40 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white w-[calc(100%-2rem)] sm:w-[95%] max-w-3xl rounded-2xl sm:rounded-[32px] shadow-2xl overflow-hidden my-auto border border-slate-100 max-h-[90vh] overflow-y-auto"
            >
              <header className="bg-[#0B1C2D] p-6 md:p-8 text-white flex justify-between items-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 md:w-48 md:h-48 bg-[#E63946] blur-[60px] md:blur-[80px] opacity-40 pointer-events-none"></div>
                <div className="relative z-10">
                  <h3 className="text-xl md:text-3xl font-black tracking-tight mb-1">
                    Let’s Discuss Your Vision
                  </h3>
                  <p className="text-slate-400 text-[9px] md:text-xs font-bold uppercase tracking-widest">
                    Serious projects start with clarity
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="relative z-10 size-8 md:size-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-[#E63946] transition-colors"
                >
                  <span className="material-symbols-outlined text-sm">
                    close
                  </span>
                </button>
              </header>

              <ServiceModal setIsModalOpen={setIsModalOpen} />
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DevelopmentPage;
